// components/Admin/TitleProbabilityManager/ProbabilityTable.jsx
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  TextField, IconButton, Box, Stack, TableContainer, useMediaQuery, useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useState } from 'react';

const ProbabilityTable = ({ tiers, onUpdate, onDelete }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedTier, setEditedTier] = useState('');
  const [editedProbability, setEditedProbability] = useState('');
  const [editedColor, setEditedColor] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const startEdit = (index, tier) => {
    setEditingIndex(index);
    setEditedTier(tier.tier);
    setEditedProbability(tier.probability);
    setEditedColor(tier.color || '#95a5a6');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditedTier('');
    setEditedProbability('');
    setEditedColor('');
  };

  const saveEdit = () => {
    if (!editedTier || editedProbability === '' || !editedColor) return;

    const isDuplicate = tiers.some((t, idx) =>
      idx !== editingIndex && t.tier.toLowerCase() === editedTier.toLowerCase()
    );
    if (isDuplicate) {
      alert(`"${editedTier}" 등급은 이미 존재합니다.`);
      return;
    }

    onUpdate(editingIndex, {
      tier: editedTier,
      probability: parseFloat(editedProbability),
      color: editedColor,
    });
    cancelEdit();
  };

  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size={isMobile ? 'small' : 'medium'}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontSize: isMobile ? '0.75rem' : undefined }}>등급</TableCell>
            <TableCell sx={{ fontSize: isMobile ? '0.75rem' : undefined }}>확률 (%)</TableCell>
            <TableCell sx={{ fontSize: isMobile ? '0.75rem' : undefined }}>색상</TableCell>
            <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : undefined }}>작업</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {tiers.map((tier, index) => (
            <TableRow key={`${tier.id ?? 'temp'}-${index}`}>
              {/* ── 등급 ───────────────────────── */}
              <TableCell>
                {editingIndex === index ? (
                  <TextField
                    value={editedTier}
                    onChange={e => setEditedTier(e.target.value)}
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Box sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>{tier.tier}</Box>
                )}
              </TableCell>

              {/* ── 확률 ──────────────────────── */}
              <TableCell>
                {editingIndex === index ? (
                  <TextField
                    type="number"
                    value={editedProbability}
                    onChange={e => setEditedProbability(e.target.value)}
                    inputProps={{ step: 0.01, min: 0 }}
                    size="small"
                    fullWidth
                  />
                ) : (
                  <Box sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>{`${tier.probability}%`}</Box>
                )}
              </TableCell>

              {/* ── 색상 ──────────────────────── */}
              <TableCell>
                {editingIndex === index ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <input
                      type="color"
                      value={editedColor}
                      onChange={e => setEditedColor(e.target.value)}
                      style={{
                        width: 32,
                        height: 32,
                        border: 'none',
                        padding: 0,
                        background: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <TextField
                      label="HEX"
                      size="small"
                      value={editedColor}
                      onChange={e => setEditedColor(e.target.value)}
                      sx={{ width: 100 }}
                    />
                  </Stack>
                ) : (
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: tier.color || '#ccc',
                      borderRadius: 1,
                      border: '1px solid #aaa',
                      display: 'inline-block',
                    }}
                    title={tier.color}
                  />
                )}
              </TableCell>

              {/* ── 작업 버튼 ─────────────────── */}
              <TableCell align="right">
                {editingIndex === index ? (
                  <>
                    <IconButton sx={{ p: isMobile ? 0.5 : 1 }} onClick={saveEdit}>
                      <DoneIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                    <IconButton sx={{ p: isMobile ? 0.5 : 1 }} onClick={cancelEdit}>
                      <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <IconButton sx={{ p: isMobile ? 0.5 : 1 }} onClick={() => startEdit(index, tier)}>
                      <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                    <IconButton sx={{ p: isMobile ? 0.5 : 1 }} onClick={() => onDelete(tier.id)}>
                      <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </IconButton>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProbabilityTable;
